import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  CalendarMonth,
  Assignment,
  EventNote,
  Schedule,
  CheckCircle,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useRecentActivities } from "../../hooks/queries/useActivity";

const getIconForActivity = (entityType, action) => {
  const type = String(entityType || "").toLowerCase();
  const act = String(action || "").toLowerCase();

  if (type === "timetable") {
    return <CalendarMonth fontSize="small" />;
  }
  if (type === "assignment" || type === "assignment_submission") {
    return <Assignment fontSize="small" />;
  }
  if (type === "exam") {
    return <EventNote fontSize="small" />;
  }
  if (type === "leave") {
    return <Schedule fontSize="small" />;
  }
  if (type === "schedule_change") {
    return <Schedule fontSize="small" />;
  }
  if (act === "approved" || act === "generated") {
    return <CheckCircle fontSize="small" />;
  }
  return <CheckCircle fontSize="small" />;
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return "";
  try {
    return `${formatDistanceToNow(new Date(isoString), { addSuffix: true })}`;
  } catch {
    return "";
  }
};

const RecentActivitiesCard = ({ title = "Recent Activities", maxItems = 10 }) => {
  const { data, isLoading, error } = useRecentActivities({ limit: maxItems });

  const activities = Array.isArray(data) ? data.slice(0, maxItems) : [];

  const showEmpty = !isLoading && !error && activities.length === 0;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        {isLoading && (
          <Box>
            {Array.from({ length: Math.min(maxItems, 5) }).map((_, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="30%" />
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {error && !isLoading && (
          <Typography variant="body2" color="text.secondary">
            We couldn’t load your recent activities. Please try again.
          </Typography>
        )}
        {showEmpty && (
          <Typography variant="body2" color="text.secondary">
            No recent activities yet.
          </Typography>
        )}
        {!isLoading && !error && activities.length > 0 && (
          <List disablePadding>
            {activities.map((activity, index) => {
              const primary = activity.title || "Activity";
              const secondary =
                activity.description ||
                formatRelativeTime(activity.createdAt) ||
                "";
              const timeLabel = formatRelativeTime(activity.createdAt);
              return (
                <React.Fragment key={activity.id || activity.uuid || index}>
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1.5,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "primary.light",
                        }}
                      >
                        {getIconForActivity(activity.entityType, activity.action)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={primary}
                      secondary={timeLabel || secondary}
                      primaryTypographyProps={{ variant: "body2" }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItem>
                  {index < activities.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivitiesCard;

